import {
  Event as SentryEvent,
  Scope,
  User as SentryUser,
  addBreadcrumb as sentryAddBreadcrumb,
  captureEvent,
  captureException,
  setContext as sentrySetContext,
  setTag as sentrySetTag,
  setTags as sentrySetTags,
  setUser,
  startInactiveSpan,
  withScope as sentryWithScope,
} from '@sentry/nextjs';

import { BreadcrumbConfig, MonitoringService } from '@kit/monitoring-core';

/**
 * @class
 * @implements {MonitoringService}
 * SentryMonitoringService provides full Sentry integration including
 * error tracking, performance monitoring, breadcrumbs, and context management.
 */
export class SentryMonitoringService implements MonitoringService {
  private readonly readyPromise: Promise<unknown>;
  private readyResolver?: (value?: unknown) => void;

  constructor() {
    this.readyPromise = new Promise(
      (resolve) => (this.readyResolver = resolve),
    );

    void this.initialize();
  }

  async ready() {
    return this.readyPromise;
  }

  captureException(
    error: Error | null,
    extra?: Record<string, unknown>,
    config?: Record<string, unknown>,
  ) {
    return sentryWithScope((scope) => {
      if (extra) {
        scope.setExtras(extra);
      }

      if (config) {
        Object.entries(config).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }

      return captureException(error);
    });
  }

  captureEvent<Extra extends SentryEvent>(event: string, extra?: Extra) {
    return captureEvent({
      message: event,
      ...(extra ?? {}),
    });
  }

  identifyUser(user: SentryUser) {
    setUser(user);
  }

  // ============================================
  // Performance Monitoring Methods
  // ============================================

  startTransaction(
    name: string,
    op: string,
    data?: Record<string, unknown>,
  ): unknown {
    return startInactiveSpan({
      name,
      op,
      attributes: data as Record<string, string | number | boolean | undefined>,
      forceTransaction: true,
    });
  }

  startSpan(
    name: string,
    op: string,
    data?: Record<string, unknown>,
  ): unknown {
    return startInactiveSpan({
      name,
      op,
      attributes: data as Record<string, string | number | boolean | undefined>,
    });
  }

  finishSpan(span: unknown, _status?: string): void {
    const sentrySpan = span as { end?: () => void };

    if (sentrySpan && typeof sentrySpan.end === 'function') {
      sentrySpan.end();
    }
  }

  finishTransaction(transaction: unknown, _status?: string): void {
    const sentryTx = transaction as { end?: () => void };

    if (sentryTx && typeof sentryTx.end === 'function') {
      sentryTx.end();
    }
  }

  // ============================================
  // Context & Tagging Methods
  // ============================================

  addBreadcrumb(breadcrumb: BreadcrumbConfig): void {
    sentryAddBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      level: breadcrumb.level ?? 'info',
      data: breadcrumb.data,
    });
  }

  setContext(name: string, context: Record<string, unknown>): void {
    sentrySetContext(name, context);
  }

  setTag(key: string, value: string): void {
    sentrySetTag(key, value);
  }

  setTags(tags: Record<string, string>): void {
    sentrySetTags(tags);
  }

  withScope<T>(callback: (scope: Scope) => T): T {
    return sentryWithScope(callback);
  }

  // ============================================
  // Initialization
  // ============================================

  private async initialize() {
    const environment =
      process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? process.env.VERCEL_ENV;

    // Only initialize browser client here - server initialization is handled
    // separately in instrumentation.ts and sentry.server.config.ts to avoid
    // bundling @sentry/node on the client side
    if (typeof document !== 'undefined') {
      const { initializeSentryBrowserClient } = await import(
        '../sentry.client.config'
      );

      initializeSentryBrowserClient({
        environment,
      });
    }
    // Server-side: Sentry is already initialized via instrumentation.ts
    // which imports from @kit/sentry/config/server

    this.readyResolver?.();
  }
}
